// ANGEL FLIGHT — master data contract (FIRST-PERSON gunner).
//
// The spine of the game alongside EventBus.ts. The camera is the gunner's eyes:
// you look DOWN your gun at a battlefield that recedes into depth. Enemies live
// in a 3D-ish world space (lateral x, height y, DEPTH z = metres ahead) and are
// projected to the screen with perspective — far = small + high + faint, near =
// big + low + sharp. Pure (no Phaser). Bump SAVE_VERSION on save-shape changes.

/** On-disk save schema version. Migrations in core/storage.ts gate on it. */
export const SAVE_VERSION = 2 as const;

/* ──────────────────────────────────────────────────────────────────────────
 * Geometry — the first-person world space
 * ──────────────────────────────────────────────────────────────────────── */

export interface Vec2 {
  x: number;
  y: number;
}

/**
 * A point in the gunner's world. `x` is lateral metres (0 = dead ahead, +right),
 * `z` is depth metres (distance from the muzzle into the scene), `y` is height
 * above the ground plane (0 = ground; >0 = airborne). Projected to the screen by
 * render/Projection.ts.
 */
export interface WorldPos {
  x: number;
  z: number;
  y: number;
}

/** A screen-space projection result (where/how big a world point draws). */
export interface Projected {
  sx: number; // screen x (px)
  sy: number; // screen y (px)
  scale: number; // perspective scale (1 at the near plane, →0 at the horizon)
  /** True if behind the near plane / off the usable frustum (cull). */
  culled: boolean;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Factions & enemies (the zombie horde)
 * ──────────────────────────────────────────────────────────────────────── */

export type Faction = 'undead' | 'civilian';

/**
 * Battlefield unit archetypes. Slice ships shambler/runner/brute (undead) plus
 * the `trooper` (a BLUEFOR friendly that must NOT be shot); the rest are reserved
 * so config + save schema stay stable for the content pass.
 *
 * NOTE: not every id is an enemy — `trooper` is a `civilian`-faction ALLY. Use
 * `getEnemy(id).faction` to tell friend from foe; `isFriendly(def)` is the helper.
 */
export type EnemyId =
  | 'shambler' // slow filler horde
  | 'runner' // fast sprinter
  | 'brute' // armored heavy, weak-point head
  | 'spitter' // ranged (reserved)
  | 'screamer' // RPG soldier — rockets are FLARE-able (key F)
  | 'marauder' // hostile rifleman — hammers the bird with fast low-damage rounds
  | 'trooper'; // BLUEFOR friendly — advances with the horde, do NOT shoot

/** Hit zone for damage multipliers (headshots matter in first-person). */
export type BodyZone = 'body' | 'head' | 'legs';

/* ──────────────────────────────────────────────────────────────────────────
 * Weapons
 * ──────────────────────────────────────────────────────────────────────── */

export type WeaponId = 'minigun' | 'autocannon' | 'railgun' | 'flamer';

/** How a weapon's tracer reads in the render layer (the lead's follow-up). */
export type TracerStyle = 'thin' | 'fat' | 'beam' | 'flame';

export interface WeaponDef {
  id: WeaponId;
  name: string;
  damage: number;
  /** Rounds per second. */
  fireRate: number;
  /** Aim spread half-angle in SCREEN px (cone widens with sustained fire). */
  spreadPx: number;
  /** Hit radius in screen px at the target's projected scale. */
  hitRadiusPx: number;
  /** Magazine / heat capacity before a vent. */
  ammo: number;
  reloadMs: number;
  /** Max effective depth (metres) a round reaches. ENFORCED by FiringSystem:
   *  a primary raycast hit (or splash candidate) beyond this depth falls short.
   *  This is what keeps the flamer a short-range weapon, not a horizon sniper. */
  rangeZ: number;

  /* ── per-weapon identity (optional; absence = plain single-target) ──────── */

  /** Railgun: the round PIERCES — it damages EVERY live enemy along the shot
   *  line (muzzle→aim) in screen space, not just the first. */
  pierce?: boolean;
  /** Autocannon: small explosive AoE — also damages enemies whose projected
   *  screen centre is within this radius (px) of the primary hit. */
  blastRadiusPx?: number;
  /** Autocannon: DEPTH window (metres) of the blast — splash only reaches
   *  enemies within this world-depth of the primary (the shell explodes AT the
   *  primary's depth; screen px alone is depth-blind at the horizon). */
  blastDepthM?: number;
  /** Flamer: wide short cone — splashes EVERY enemy whose projected screen
   *  centre is within this radius (px) of the aim point each shot. Candidates
   *  (and the primary) beyond `rangeZ` metres are out of reach — the cone is a
   *  SHORT-range volume, not a horizon-spanning screen circle. */
  coneSpreadPx?: number;
  /** How the tracer reads in the render layer (read by the lead's VFX). */
  tracerStyle?: TracerStyle;
  /** Multiplies the base screenshake/recoil punch per shot (1 = default). */
  recoilScale?: number;
  /** Barrel spin-up time before the gun reaches full fire-rate (ms). */
  spinupMs?: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Upgrades (the Armory)
 * ──────────────────────────────────────────────────────────────────────── */

export type UpgradeId =
  | 'damage'
  | 'fireRate'
  | 'ammoCap'
  | 'reload'
  | 'hull'
  | 'autoRepair';

export type UpgradeTier = 0 | 1 | 2 | 3 | 4 | 5;
export type UpgradeBranch = 'gun' | 'bird';

export interface UpgradeDef {
  id: UpgradeId;
  branch: UpgradeBranch;
  name: string;
  blurb: string;
  costs: readonly number[];
  values: readonly number[];
}

/* ──────────────────────────────────────────────────────────────────────────
 * Difficulty
 * ──────────────────────────────────────────────────────────────────────── */

export type Difficulty = 'recruit' | 'pilot' | 'ace';

export interface DifficultyDef {
  id: Difficulty;
  name: string;
  hullMult: number;
  densityMult: number;
  payoutMult: number;
  /** Depth (metres) at which enemies become FULLY visible through the
   *  battlefield haze. Spawns sit beyond it and fade in as they close — lower
   *  = enemies emerge from the fog closer to the bird (harder). The render
   *  layer drives the rig fade + the mist band off this. */
  revealZM: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Player / run state
 * ──────────────────────────────────────────────────────────────────────── */

export interface PlayerStats {
  maxHull: number;
  damageMult: number;
  fireRateMult: number;
  ammoCap: number;
  reloadMult: number;
  /** Hull HP regenerated per second once the bird has been out of contact for
   *  BALANCE.player.autoRepairDelayMs (0 = no regen — the un-upgraded default). */
  autoRepair: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Missions
 * ──────────────────────────────────────────────────────────────────────── */

export type ObjectiveKind = 'survive' | 'boss' | 'endless';

export interface WaveDef {
  atMs: number;
  spawns: Partial<Record<EnemyId, number>>;
  banner?: string;
}

export interface MissionDef {
  id: string;
  name: string;
  biome: string;
  objective: ObjectiveKind;
  durationMs: number;
  waves: readonly WaveDef[];
  brief: string;
  /** Sky/ground theme key for the parallax backdrop. */
  theme: 'dusk-suburb' | 'night-city' | 'dead-forest' | 'frozen-checkpoint';
}

export interface RunStats {
  kills: number;
  scrip: number;
  salvage: number;
  bestCombo: number;
  headshots: number;
  elapsedMs: number;
  /** Friendly-fire STRIKES (debounced incidents, not rounds — one burst-window
   *  sweep across a BLUEFOR trooper counts ONCE). The Nth strike fails the run. */
  friendliesHit: number;
  /** Rounds fired this run (accuracy denominator). */
  shotsFired: number;
  /** Rounds that damaged at least one HOSTILE (friendly hits never count). */
  shotsHit: number;
}

export interface MissionResult {
  missionId: string;
  cleared: boolean;
  /** The mode's objective kind (defaults to 'survive' when absent — older
   *  callers/tests). 'endless' runs end on hull-down but are NOT a "wipe": the
   *  grade skips the fail-score cap and the run banks its best (see grading +
   *  GameLoop). */
  objective?: ObjectiveKind;
  stats: RunStats;
  /** Graded mission score (config/grading.ts — the single score formula). The
   *  Debrief shows it and derives the letter grade from it. */
  score: number;
  /** True if this run beat the previously persisted best for the mission (the
   *  prev best is read BEFORE banking, so the NEW BEST flash is honest). */
  newBest: boolean;
  /** The persisted best score BEFORE this run banked (0 if none). The Debrief
   *  passes this into the medal evaluation so NEW BEST stays honest. */
  prevBest: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Settings & save
 * ──────────────────────────────────────────────────────────────────────── */

export interface GameSettings {
  master: number;
  sfx: number;
  music: number;
  screenshake: boolean;
  hitstop: boolean;
}

export interface SaveStateV1 {
  version: typeof SAVE_VERSION;
  difficulty: Difficulty;
  scrip: number;
  salvage: number;
  equippedWeapon: WeaponId;
  ownedWeapons: WeaponId[];
  upgrades: Record<UpgradeId, UpgradeTier>;
  missionProgress: number;
  bestScores: Record<string, number>;
  settings: GameSettings;
  /** The "LAST BIRD UP" intro has played (or been skipped) once on this save. */
  introSeen: boolean;
}

export interface GameSceneData {
  missionId: string;
}

export interface DebriefSceneData {
  result: MissionResult;
}
